import _ from 'lodash';
import { routeTo } from 'redux-router-kit';

import { addParamsToHash, addTruthyParamsToUrl } from '@zapier/url-utils';

import * as Account from 'app/entities/Account';
import * as MassActionFeedbackChannel from 'app/entities/MassActionFeedbackChannel';
import * as Profile from 'app/entities/Profile';
import ActionTypes from 'app/history/ActionTypes';
import AuthActions from 'app/auth/AuthActions';
import DashboardActions from 'app/dashboard/DashboardActions';
import Data from 'app/history/Data';
import FlowData from 'app/beta-editor/Data';
import { ZAP_FILTER_SPECIAL_VALUES } from 'app/history/utils/constants';
import { getCurrentPathname } from 'app/common/utils/RouterSelectors';
import { willPromisify } from 'app/common/middlewares/willPromiseMiddleware';
import { selectDeletedTasks, selectStaleAuthIds } from 'app/history/Selectors';

const actions = {
  maybeSwitchAccountsBasedOnZapId(queryParams) {
    return (dispatch, getState) => {
      const rootId = _.get(queryParams, 'root_id');
      if (rootId && !(rootId in ZAP_FILTER_SPECIAL_VALUES)) {
        FlowData.zap(rootId)
          .then(zap => {
            if (
              zap.account_id !==
              Account.selectors.all.currentAccountId(getState())
            ) {
              const message =
                'Tasks for requested Zap belong to a different account, so switching to that account…';
              dispatch(Account.actions.switchAccount(zap.account_id, message));
            }
          })
          .catch(_.noop);
      }
    };
  },
  loadTasks(queryParams) {
    return (dispatch, getState) => {
      // Switch accounts if tasks for a zap that belongs to a different account
      // are requested. It's fine not to wait on this since account switching
      // will reload the page.
      dispatch(actions.maybeSwitchAccountsBasedOnZapId(queryParams));
      return dispatch(
        willPromisify(
          Data.tasks,
          [
            Account.selectors.all.currentAccountId,
            state =>
              Profile.selectors.timezone(
                Profile.selectors.all.currentProfile(state)
              ),
            queryParams,
          ],
          {
            type: ActionTypes.LOAD_TASKS,
            resultKey: 'tasks',
          }
        )
      ).then(() => {
        const staleAuths = selectStaleAuthIds(getState());
        if (staleAuths.length) {
          dispatch(AuthActions.loadAuths());
        }
      });
    };
  },

  sendCSV(queryParams, { onDangerouslyReceiveSuccess }) {
    return willPromisify(
      Data.sendCSV,
      [
        Account.selectors.all.currentAccountId,
        state =>
          Profile.selectors.timezone(
            Profile.selectors.all.currentProfile(state)
          ),
        queryParams,
      ],
      {
        type: ActionTypes.SEND_CSV,
        queryParams,
        onDangerouslyReceiveSuccess,
      }
    );
  },

  loadTask(taskId) {
    return dispatch =>
      dispatch({
        type: ActionTypes.LOAD_TASK,
        taskId,
        promise: Data.task(taskId).then(task => {
          if (task.account_id) {
            dispatch(
              Account.actions.setAndStoreCurrentAccountId(task.account_id)
            );
          }
          return task;
        }),
        resultKey: 'task',
      });
  },

  replayTask(taskId) {
    return willPromisify(
      Data.replayTask,
      [Account.selectors.all.currentAccountId, taskId],
      {
        taskId,
        types: [ActionTypes.REPLAY_TASK, ActionTypes.REPLAY_TASK_DONE],
        resultKey: 'success',
      }
    );
  },

  selectShown() {
    return {
      type: ActionTypes.SELECT_SHOWN,
    };
  },

  selectAll() {
    return {
      type: ActionTypes.SELECT_ALL,
    };
  },

  routeToThenSelectAll(route) {
    return dispatch => {
      dispatch(routeTo(route)).then(() => {
        dispatch(this.selectAll());
      });
    };
  },

  unselectAll() {
    return {
      type: ActionTypes.UNSELECT_ALL,
    };
  },

  toggleSelectTask(taskId) {
    return {
      type: ActionTypes.TOGGLE_SELECT_TASK,
      taskId,
    };
  },

  onMassAction(action, queryParams) {
    return dispatch => {
      return dispatch(
        willPromisify(
          Data.massAction,
          [
            action,
            Account.selectors.all.currentAccountId,
            state =>
              Profile.selectors.timezone(
                Profile.selectors.all.currentProfile(state)
              ),
            queryParams,
          ],
          {
            type: ActionTypes.MASS_ACTION,
          }
        )
      ).then(({ result }) => {
        dispatch(
          MassActionFeedbackChannel.actions.createFeedbackChannel(
            {
              action,
              channelId: result.channel,
              channelType: 'taskHistoryMassAction',
              unreplayableErrors: result.errors,
              globalFailures: result.globalFailures,
              globalFailureType: result.globalFailureType,
              isMassActionsDisabled: result.isMassActionsDisabled,
            },
            () => {
              dispatch(actions.markSuccessfulTasks());
              dispatch(actions.markDeletedTasks());
              dispatch(actions.unselectAll());
              dispatch(DashboardActions.loadHeldReasons());
            }
          )
        );
      });
    };
  },

  markSuccessfulTasks() {
    return (dispatch, getState) => {
      const successfulTaskIds = MassActionFeedbackChannel.selectors.all.taskReplayChannelSuccessfulTaskIds(
        getState()
      );

      if (successfulTaskIds.length > 0) {
        dispatch({
          type: ActionTypes.MARK_SUCCESSFUL_TASKS,
          successfulTaskIds,
        });
      }
    };
  },

  markDeletedTasks() {
    return (dispatch, getState) => {
      return dispatch({
        type: ActionTypes.MARK_DELETED_TASKS,
        deletedTaskIds: selectDeletedTasks(getState()).map(t => t.id),
      });
    };
  },

  closeSelectionDetails() {
    return { type: ActionTypes.CLOSE_SELECTION_DETAILS };
  },

  updateHashParams(hashParams) {
    return (dispatch, getState) => {
      const currentPathname = getCurrentPathname(getState());
      if (!_.isEmpty(hashParams)) {
        const urlWithUpdatedHashParams = addParamsToHash(
          currentPathname,
          hashParams
        );
        dispatch(routeTo(urlWithUpdatedHashParams));
      } else {
        dispatch(routeTo(currentPathname));
      }
    };
  },

  goToPath(pathId, nodeId) {
    return (dispatch, getState) => {
      const currentPathname = getCurrentPathname(getState());
      const newUrl = addTruthyParamsToUrl(currentPathname, { pathId, nodeId });
      dispatch(routeTo(newUrl));
    };
  },

  clearSubBranch() {
    return (dispatch, getState) => {
      const currentPathname = getCurrentPathname(getState());
      return dispatch(routeTo(currentPathname));
    };
  },
};

export default actions;
